import { useCallback, useMemo, useState } from 'react';

import { NULL_ADDRESS } from 'shared/constants/constants';
import { useAccount, usePublicClient, useWalletClient } from 'shared/hooks/use-web3';
import { useCurrenciesStore } from 'shared/store/currenciesStore';
import { useStartGameStore } from 'shared/store/start-game-store';
import { CurrencyType } from 'shared/web3/utils/getCurrencyInfo';

import { Analytics } from '@/lib/AnalyticsLoader/analytics';

interface IuseQuickGameProps {
  wins?: number;
  currency: CurrencyType;
  amount: number;
  startGameHandler: (bet: number, wins: number, playerB: string, currency?: CurrencyType) => void;
}

export const useQuickGame = ({ amount, currency, wins, startGameHandler }: IuseQuickGameProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { currentAllowance, approveAllowance } = useStartGameStore();
  const walletClient = useWalletClient();
  const publicClient = usePublicClient();
  const { address } = useAccount();
  const { selectedCurrency } = useCurrenciesStore();
  const normalizedAmount = useMemo(
    () => amount / 10 ** (currency?.decimals ?? 18),
    [amount, currency],
  );

  const createGameHandler = useCallback(async () => {
    Analytics.quickGameStartEvent(address, amount, currency.symbol);
    if (selectedCurrency && selectedCurrency.balance >= normalizedAmount) {
      setIsLoading(true);
      try {
        if (Number(currentAllowance) < normalizedAmount && approveAllowance) {
          await approveAllowance({
            amount,
            currency: currency,
            userAddress: address as `0x${string}`,
            walletClient,
            publicClient,
            fullAllowance: false,
            joinGame: true,
          });
        }
        if (startGameHandler) {
          startGameHandler(normalizedAmount, wins, NULL_ADDRESS, currency);
        }
      } catch (error) {
        console.log('Error:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [
    address,
    amount,
    selectedCurrency,
    currentAllowance,
    normalizedAmount,
    publicClient,
    startGameHandler,
    walletClient,
    wins,
    approveAllowance,
    currency,
  ]);
  const gameType = useMemo(
    () =>
      wins === 3
        ? 'quickGame.shortGame'
        : wins === 6
          ? 'quickGame.mediumGame'
          : 'quickGame.longGame',
    [wins],
  );

  return {
    createGameHandler,
    gameType,
    isLoading,
    normalizedAmount,
  };
};
